<template>
  <div class="info-progress-wrapper">
    <div class="progress-circle-container">
      <svg
        class="progress-circle"
        width="24"
        height="24"
        viewBox="0 0 24 24"
      >
        <circle
          class="progress-circle-bg"
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke-width="2"
        ></circle>
        <circle
          class="progress-circle-value"
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke-width="2"
          :style="{ strokeDashoffset: progressOffset }"
        ></circle>
      </svg>
      <span class="info-icon">i</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InfoProgressCircle',
  props: {
    progressOffset: {
      type: Number,
      default: 62.8 // Default to 0% progress (full offset)
    },
    backgroundColor: {
      type: String,
      default: '#337ab7' // Default blue color
    }
  },
  mounted() {
    // Update the color variables when component is mounted
    this.updateBackgroundColorVar();
  },
  watch: {
    // Watch for changes in the background color prop
    backgroundColor: {
      handler() {
        // Update color variables when the prop changes
        this.updateBackgroundColorVar();
      },
      immediate: true
    }
  },
  methods: {
    // Method to update CSS variable for background color
    updateBackgroundColorVar() {
      // Extract the color from props
      const bgColor = this.backgroundColor;
      
      // Set it as a CSS variable
      document.documentElement.style.setProperty('--header-bg-color', bgColor);
      
      // Calculate the darkened version for hover state
      const darkenedColor = this.calculateDarkenedColor(bgColor);
      document.documentElement.style.setProperty('--header-hover-bg-color', darkenedColor);

      // Also set the base color even if CSS variables aren't supported
      document.documentElement.style.setProperty('--header-base-color', bgColor);
    },
    
    // Method to calculate the darkened version of a color
    calculateDarkenedColor(color) {
      // Default darkened blue as fallback
      let darkenedColor = 'rgb(25, 80, 129)';
      
      // Try to parse the input color
      try {
        // Handle hex color format
        if (color.startsWith('#')) {
          // Convert hex to RGB
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          
          // Apply channel-specific darkening factors for more natural shadow
          const darkR = Math.round(r * 0.85);   // 15% darker for red
          const darkG = Math.round(g * 0.944);  // ~5.6% darker for green
          const darkB = Math.round(b * 0.973);  // ~2.7% darker for blue
          
          darkenedColor = `rgb(${darkR}, ${darkG}, ${darkB})`;
        }
        // Handle rgb/rgba color format
        else if (color.startsWith('rgb')) {
          // Extract RGB values using regex
          const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
          if (rgbMatch) {
            const r = parseInt(rgbMatch[1]);
            const g = parseInt(rgbMatch[2]);
            const b = parseInt(rgbMatch[3]);
            
            // Apply channel-specific darkening factors for more natural shadow
            const darkR = Math.round(r * 0.85);   // 15% darker for red
            const darkG = Math.round(g * 0.944);  // ~5.6% darker for green
            const darkB = Math.round(b * 0.973);  // ~2.7% darker for blue
            
            darkenedColor = `rgb(${darkR}, ${darkG}, ${darkB})`;
          }
        }
      } catch (e) {
        console.error('Error calculating darkened color:', e);
      }
      
      return darkenedColor;
    }
  }
}
</script>

<style>
/* Info progress indicator styles */
.info-progress-wrapper {
  position: absolute;
  right: 7px;
  top: 68%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.progress-circle-container {
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-circle-container::before {
  content: "";
  position: absolute;
  width: 30px;
  height: 30px;
  background-color: var(--header-hover-bg-color, var(--header-base-color, #337ab7));
  opacity: 1;
  border-radius: 50%;
  filter: blur(5px);
  z-index: -1;
}

.progress-circle {
  transform: rotate(-90deg);
}

.progress-circle-bg {
  stroke: rgba(255, 255, 255, 0.3);
}

.progress-circle-value {
  stroke: white;
  stroke-dasharray: 62.8; /* 2πr where r=10 */
  stroke-dashoffset: 62.8; /* Start at 0% progress */
  transition: stroke-dashoffset 0.1s linear;
}

.info-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-style: italic;
}
</style> 